<template>
  <div class="flex flex-col">
    <div class="grid grid-cols-12 gap-4">
      <AppModal v-model="showAddCountry" title="Add Country">
        <NewCountry />
      </AppModal>

      <AppModal v-model="showEditCountry" title="Edit Country">
        <form @submit.prevent="updateCountry">
          <div class="space-y-4">
            <div>
              <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Country Name</label>
              <input
                type="text"
                v-model="form.name"
                placeholder="Country Name"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
                required
              />
            </div>
            <div>
              <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Country Code</label>
              <input
                type="text"
                v-model="form.code"
                placeholder="Country code eg. UG, US"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
                required
              />
              <input type="hidden" v-model="form.id" />
            </div>
            <div class="flex justify-end space-x-2">
              <button
                type="button"
                @click="closeEditForm"
                class="btn-sm-primary"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="loading"
                class="btn-primary"
              >
                <i class="fa-solid fa-save"></i>
                <span>Save</span>
              </button>
            </div>
          </div>
        </form>
      </AppModal>
    </div>

    <div class="grid grid-cols-12 gap-4">
      <div class="col-span-12">
        <div class="flex items-center gap-2 justify-end mb-4">
          <button
            @click="openForm"
            class="btn-primary"
          >
            <i class="fa-solid fa-plus"></i>
            <span>Add Country</span>
          </button>
        </div>

        <div class="bg-white rounded border border-gray-200 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full text-xs">
              <thead class="bg-gray-50 sticky top-0">
                <tr>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-16">#</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Name</th>
                  <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(country, idx) in countries"
                  :key="country.id"
                  class="hover:bg-gray-50 transition"
                >
                  <td class="py-3 px-4 border-b border-gray-200">{{ idx + 1 }}</td>
                  <td class="py-3 px-4 border-b border-gray-200 capitalize font-medium">{{ country.name }}</td>
                  <td class="py-3 px-4 border-b border-gray-200">
                    <div class="flex items-center justify-center gap-2">
                      <button
                        @click="editCountry(country)"
                        class="btn-sm-primary"
                      >
                        <i class="fa-solid fa-pencil"></i>
                        <span>Edit</span>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="countries.length === 0">
                  <td colspan="3" class="py-8 px-4 text-center text-gray-500">
                    No countries found. Click "Add Country" to create one.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useConfigurationsStore } from "../../../stores";
import AppModal from "@/components/ui/AppModal.vue";
import NewCountry from "@/domains/home/services/configurations/views/settings/countries/NewCountry.vue";

interface Country {
  id: string
  name: string
  code: string
}

interface CountryForm {
  id: string | null
  code: string | null
  name: string | null
}

const store = useConfigurationsStore();
const showAddCountry = ref(false)
const showEditCountry = ref(false)
const countries = ref<Country[]>([])
const loading = ref(false)

const form = ref<CountryForm>({
  id: null,
  code: null,
  name: null
})

const openForm = () => {
  showAddCountry.value = true
}

const editCountry = (country: Country) => {
  form.value = {
    id: country.id,
    code: country.code,
    name: country.name
  }
  showEditCountry.value = true
}

const closeEditForm = () => {
  showEditCountry.value = false
  form.value = {
    id: null,
    code: null,
    name: null
  }
}

const updateCountry = () => {
  if (!form.value.name || !form.value.code) {
    return
  }

  loading.value = true
  // Assuming there's an updateCountry action in the store
  store.updateCountry(form.value);
}

const fetchData = () => {
  store.fetchCountries();
}

onMounted(() => {
  fetchData()
})

watch(
  () => store.countries,
  (data) => {
    if (data) {
      countries.value = data
    }
  }
)

watch(
  () => store.country,
  (country) => {
    if (country) {
      showAddCountry.value = false
      showEditCountry.value = false
      loading.value = false
      fetchData()
    }
  }
)
</script>

